import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Search, FileText, ChevronDown, ChevronRight,
  DollarSign, Calendar, User, Building2, Filter,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlatformLayout } from "./platform-layout";

interface FinancialAuditEntry {
  id: string;
  workspaceId: string;
  workspaceName?: string;
  entityType: "invoice" | "deposit" | "payment";
  entityId: string;
  action: string;
  actorUserId: string | null;
  actorEmail: string | null;
  actorType: "user" | "system" | "platform_admin";
  entitySnapshot: Record<string, any> | null;
  changedFields: Record<string, { old: any; new: any }> | null;
  metadata: Record<string, any> | null;
  createdAt: string;
}

const ACTION_META: Record<string, { label: string; color: string }> = {
  invoice_created:   { label: "Invoice Created",   color: "bg-green-500" },
  invoice_updated:   { label: "Invoice Updated",   color: "bg-blue-500" },
  invoice_deleted:   { label: "Invoice Deleted",   color: "bg-red-500" },
  invoice_paid:      { label: "Invoice Paid",      color: "bg-emerald-600" },
  invoice_voided:    { label: "Invoice Voided",    color: "bg-orange-500" },
  invoice_cancelled: { label: "Invoice Cancelled", color: "bg-red-600" },
  deposit_recorded:  { label: "Deposit Recorded",  color: "bg-teal-600" },
  deposit_updated:   { label: "Deposit Updated",   color: "bg-blue-400" },
  balance_paid:      { label: "Balance Paid",      color: "bg-emerald-500" },
};

function formatValue(v: any): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function DiffRow({ field, old: oldVal, new: newVal }: { field: string; old: any; new: any }) {
  return (
    <div className="grid grid-cols-[180px_1fr_1fr] gap-2 text-xs py-1 border-b border-border/40 last:border-0">
      <span className="font-mono text-muted-foreground truncate">{field}</span>
      <span className="text-red-500 truncate" title={formatValue(oldVal)}>{formatValue(oldVal)}</span>
      <span className="text-green-600 truncate" title={formatValue(newVal)}>{formatValue(newVal)}</span>
    </div>
  );
}

function SnapshotBlock({ snapshot }: { snapshot: Record<string, any> }) {
  const FINANCIAL_FIELDS = [
    "invoiceNumber", "accountName", "eventRate", "discount", "dateOfEvent",
    "status", "isPaid", "paymentType", "paymentNotes", "datePaid",
    "depositRequired", "depositAmountCents", "depositMethod", "depositPaidAt", "depositNotes",
    "billingNotes", "billingEmail", "billingContactName",
    "cancelledAt", "cancellationReason",
  ];
  const relevant = FINANCIAL_FIELDS.filter((f) => f in snapshot);
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs mt-1">
      {relevant.map((f) => (
        <div key={f} className="flex gap-1 min-w-0">
          <span className="font-mono text-muted-foreground shrink-0">{f}:</span>
          <span className="truncate" title={formatValue(snapshot[f])}>{formatValue(snapshot[f])}</span>
        </div>
      ))}
    </div>
  );
}

function AuditRow({ entry }: { entry: FinancialAuditEntry }) {
  const [open, setOpen] = useState(false);
  const meta = ACTION_META[entry.action] ?? { label: entry.action, color: "bg-slate-500" };
  const hasDiff = entry.changedFields && Object.keys(entry.changedFields).length > 0;
  const hasSnapshot = !!entry.entitySnapshot;
  const canExpand = hasDiff || hasSnapshot;

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => canExpand && setOpen((o) => !o)}
        className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors ${canExpand ? "cursor-pointer" : "cursor-default"}`}
        data-testid={`financial-audit-row-${entry.id}`}
      >
        {canExpand ? (
          open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        ) : (
          <span className="w-3.5" />
        )}

        <span className={`w-2 h-2 rounded-full shrink-0 ${meta.color}`} />

        <div className="flex-1 min-w-0 grid grid-cols-[1fr_auto] gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-medium text-sm">{meta.label}</span>
            <span className="text-xs text-muted-foreground font-mono truncate">
              {entry.metadata?.invoiceNumber
                ? `#${entry.metadata.invoiceNumber}`
                : entry.entityId.slice(0, 8) + "…"}
            </span>
            {entry.metadata?.accountName && (
              <span className="text-xs text-muted-foreground truncate hidden md:block">
                · {entry.metadata.accountName}
              </span>
            )}
            {entry.metadata?.amountCents && (
              <span className="text-xs text-emerald-600 font-medium">
                ${(Number(entry.metadata.amountCents) / 100).toFixed(2)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
            <span className="hidden sm:block">
              {entry.workspaceName ?? entry.workspaceId.slice(0, 8)}
            </span>
            <span>·</span>
            <span>{entry.actorEmail ?? entry.actorType}</span>
            <span>·</span>
            <span>{new Date(entry.createdAt).toLocaleString()}</span>
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-border bg-muted/20 px-4 py-3 space-y-3 text-sm">
          {hasDiff && (
            <div>
              <div className="text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Changed Fields</div>
              <div className="grid grid-cols-[180px_1fr_1fr] gap-2 text-xs font-medium text-muted-foreground mb-1">
                <span>Field</span><span className="text-red-400">Before</span><span className="text-green-500">After</span>
              </div>
              {Object.entries(entry.changedFields!).map(([field, { old: o, new: n }]) => (
                <DiffRow key={field} field={field} old={o} new={n} />
              ))}
            </div>
          )}
          {hasSnapshot && (
            <div>
              <div className="text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">
                {hasDiff ? "Snapshot After Change" : "Full Snapshot"}
              </div>
              <SnapshotBlock snapshot={entry.entitySnapshot!} />
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            Entity ID: <span className="font-mono">{entry.entityId}</span>
            {entry.actorUserId && (
              <> · Actor ID: <span className="font-mono">{entry.actorUserId}</span></>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function FinancialAuditContent() {
  const [search, setSearch] = useState("");
  const [entityType, setEntityType] = useState("all");
  const [action, setAction] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (entityType !== "all") params.set("entityType", entityType);
  if (action !== "all") params.set("action", action);
  if (startDate) params.set("startDate", startDate);
  if (endDate) params.set("endDate", endDate);
  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String(page * PAGE_SIZE));

  const { data, isLoading } = useQuery<{ entries: FinancialAuditEntry[]; total: number }>({
    queryKey: ["/api/platform/financial-audit", search, entityType, action, startDate, endDate, page],
    queryFn: () => fetch(`/api/platform/financial-audit?${params}`, { credentials: "include" }).then((r) => r.json()),
  });

  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-600" />
            Financial Audit Log
          </CardTitle>
          <CardDescription>
            Immutable ledger of every invoice, deposit, and payment change across all workspaces.
            Append-only — no record can be edited or deleted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Invoice #, account name, entity ID, actor email…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="pl-9"
                data-testid="input-financial-audit-search"
              />
            </div>
            <Select value={entityType} onValueChange={(v) => { setEntityType(v); setPage(0); }}>
              <SelectTrigger className="w-36" data-testid="select-entity-type">
                <SelectValue placeholder="Entity type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="invoice">Invoice</SelectItem>
                <SelectItem value="deposit">Deposit</SelectItem>
              </SelectContent>
            </Select>
            <Select value={action} onValueChange={(v) => { setAction(v); setPage(0); }}>
              <SelectTrigger className="w-44" data-testid="select-action">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {Object.entries(ACTION_META).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
              className="w-36"
              data-testid="input-start-date"
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
              className="w-36"
              data-testid="input-end-date"
            />
            {(search || entityType !== "all" || action !== "all" || startDate || endDate) && (
              <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setEntityType("all"); setAction("all"); setStartDate(""); setEndDate(""); setPage(0); }}>
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
        <span>{isLoading ? "Loading…" : `${total.toLocaleString()} entries`}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="button-prev-page">← Prev</Button>
          <span className="px-2 py-1.5 text-xs">Page {page + 1}</span>
          <Button variant="outline" size="sm" disabled={entries.length < PAGE_SIZE} onClick={() => setPage((p) => p + 1)} data-testid="button-next-page">Next →</Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-14 bg-muted/40 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="font-medium text-muted-foreground">No entries found</p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              {search || entityType !== "all" || action !== "all"
                ? "Try broadening your filters."
                : "Financial events will appear here as they occur."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <AuditRow key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlatformFinancialAudit() {
  return (
    <PlatformLayout>
      <FinancialAuditContent />
    </PlatformLayout>
  );
}
